import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import stringSimilarity from 'string-similarity';
import { trackSearchArticle } from '../tracks';
import { FlattenedArticle } from '../types';
import useHelpAndDocumentationData from './useHelpAndDocumentationData';

export const useHelpAndDocumentationSearch = () => {
  const { indexedData: articles } = useHelpAndDocumentationData();
  let [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState<FlattenedArticle[]>([]);

  const similarityScoring = (article: FlattenedArticle, query: string) => {
    const queryTokens = query.toLowerCase().split(' ');
    const title = article.title.toLowerCase();
    const content = article.content.toLowerCase();

    let score = 0;

    for (const token of queryTokens) {
      if (title.includes(token)) {
        score += 2;
      }
      if (content.includes(token)) {
        score += 1;
      }
    }
    return score;
  };

  const getSuggestion = (
    query: string,
    threshold: number = 0.3,
    maxResults: number = Infinity,
  ) => {
    const suggestions: Array<{ item: FlattenedArticle; similarity: number }> =
      [];

    // Iterate over each item in the JSON data
    for (const item of articles) {
      const value = item.title; // Get the value of the specified key

      // Calculate the similarity between the query and the specified key's value
      const similarity =
        stringSimilarity.compareTwoStrings(query, value as unknown as string) +
        similarityScoring(item, query);

      // If the similarity is above the threshold, consider it a match
      if (similarity >= threshold) {
        suggestions.push({ item, similarity });
      }

      // If the maximum number of suggestions has been reached, break out of the loop
      if (suggestions.length >= maxResults) {
        break;
      }
    }

    // Sort the results by similarity in descending order
    suggestions.sort((a, b) => b.similarity - a.similarity);

    return suggestions.slice(0, 10).map(result => result.item.title);
  };

  const searchArticles = (query: string) => {
    const searchResults: { article: FlattenedArticle; score: number }[] = [];

    setSearchParams(params => {
      params.set('search', query);
      return params;
    });

    for (const article of articles) {
      var score = similarityScoring(article, query);
      if (score > 0) {
        searchResults.push({ article, score });
      }
    }

    searchResults.sort((a, b) => b.score - a.score);
    trackSearchArticle(query, searchResults.length);
    setResults(searchResults.slice(0, 7).map(result => result.article));
  };

  const searchQuery = searchParams.get('search') ?? undefined;
  const isSearching = !!searchQuery;

  useEffect(() => {
    if (isSearching) searchArticles(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { searchQuery, searchArticles, results, isSearching, getSuggestion };
};
